import { useState } from "react";
import { X, Send, MessageSquare } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/lib/platform";

const APP_VERSION = "1.5.5";

const CATEGORIES = [
    { value: "bug",    label: "Something's broken" },
    { value: "idea",   label: "Idea / feature request" },
    { value: "praise", label: "Compliment / praise" },
    { value: "other",  label: "Something else" },
];

function detectPlatform() {
    if (isNativeApp()) {
        const ua = (navigator.userAgent || "").toLowerCase();
        if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod")) return "ios";
        if (ua.includes("android")) return "android";
        return "native";
    }
    return "web";
}

export default function FeedbackModal({ open, onClose }) {
    const { user } = useAuth();
    const [category, setCategory] = useState("idea");
    const [message, setMessage] = useState("");
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    if (!open) return null;

    const close = () => {
        setMessage(""); setErr(""); setDone(false); setBusy(false);
        onClose();
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (message.trim().length < 4) {
            setErr("A few more words would help us help you.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/feedback", {
                message: message.trim(),
                category,
                app_version: APP_VERSION,
                platform: detectPlatform(),
                page: window.location.pathname,
                name: user ? undefined : (name.trim() || undefined),
                email: user ? undefined : (email.trim() || undefined),
            });
            setDone(true);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't send right now — try again or email captain@bosunsmate.app.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="feedback-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="feedback-title"
            onClick={close}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(5,15,28,0.78)",
                backdropFilter: "blur(8px)",
                display: "grid", placeItems: "center",
                zIndex: 250, padding: 16,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bm-card"
                style={{
                    maxWidth: 520, width: "100%",
                    maxHeight: "calc(100vh - 32px)",
                    overflowY: "auto",
                    padding: 0,
                    border: "1px solid var(--bm-gold-dim)",
                    boxShadow: "0 24px 70px -20px rgba(0,0,0,0.6)",
                }}
            >
                <div style={{
                    padding: "22px 24px 14px",
                    borderBottom: "1px solid var(--bm-border-soft)",
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14,
                }}>
                    <div>
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                            color: "var(--bm-gold)", marginBottom: 6,
                        }}>
                            <MessageSquare size={12} /> Captain to captain
                        </div>
                        <h2 id="feedback-title" className="bm-serif" style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>
                            {done ? "Thank you" : "Send us a note"}
                        </h2>
                    </div>
                    <button
                        data-testid="feedback-close-btn"
                        type="button" onClick={close} aria-label="Close"
                        className="bm-btn bm-btn-ghost"
                        style={{ padding: "6px 8px" }}
                    >
                        <X size={16} />
                    </button>
                </div>

                {done ? (
                    <div style={{ padding: "30px 28px 28px", textAlign: "center" }}>
                        <p style={{ margin: "0 0 18px", color: "var(--bm-text-muted)", lineHeight: 1.55, fontSize: 14 }}>
                            We read everything. If you left an email and it needs a reply, you'll hear back within a day or two from <b>captain@bosunsmate.app</b>.
                        </p>
                        <button
                            data-testid="feedback-dismiss-btn"
                            type="button" onClick={close}
                            className="bm-btn bm-btn-gold"
                            style={{ padding: "10px 24px" }}
                        >
                            Back to the helm
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} style={{ padding: "20px 24px 24px" }}>
                        <p style={{ margin: "0 0 16px", color: "var(--bm-text-muted)", fontSize: 13.5, lineHeight: 1.5 }}>
                            Spotted a bug, got an idea, or just want to say hello?
                            We read every message — usually within a day.
                        </p>

                        <label className="bm-label">What kind of note?</label>
                        <select
                            data-testid="feedback-category-select"
                            className="bm-input"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            style={{ marginBottom: 14 }}
                        >
                            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>

                        <label className="bm-label">Your note</label>
                        <textarea
                            data-testid="feedback-message-input"
                            className="bm-input"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            maxLength={4000}
                            data-gramm="false"
                            data-gramm_editor="false"
                            data-enable-grammarly="false"
                            spellCheck={false}
                            placeholder="What did you see? What did you expect?"
                            style={{ marginBottom: 14, resize: "vertical" }}
                            required
                        />

                        {!user && (
                            <>
                                <label className="bm-label">Your name <span style={{ color: "var(--bm-text-dim)", fontWeight: 400 }}>(optional)</span></label>
                                <input
                                    data-testid="feedback-name-input"
                                    className="bm-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    style={{ marginBottom: 12 }}
                                />
                                <label className="bm-label">Your email <span style={{ color: "var(--bm-text-dim)", fontWeight: 400 }}>(if you want a reply)</span></label>
                                <input
                                    data-testid="feedback-email-input"
                                    type="email"
                                    className="bm-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{ marginBottom: 14 }}
                                />
                            </>
                        )}

                        {err && (
                            <p data-testid="feedback-error" style={{ margin: "0 0 12px", color: "#e76f51", fontSize: 13 }}>{err}</p>
                        )}

                        <button
                            data-testid="feedback-submit-btn"
                            type="submit"
                            disabled={busy}
                            className="bm-btn bm-btn-gold"
                            style={{ width: "100%", justifyContent: "center", padding: "12px 16px", opacity: busy ? 0.6 : 1 }}
                        >
                            <Send size={14} /> {busy ? "Sending…" : "Send to the founder"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
